import { useLayoutEffect, useMemo, useRef } from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import { Table } from '~/components/common';

import { useClaimStore } from '~/store';
import {
  type TypeReturnGoodsHandlers,
  type TypeReturnGoodsRow,
  getClaimReturnRegisterReturnItemsColumnDefs,
} from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type { TypeReturnRegisterSecondStepValues, TypeReturnRegisterSecondStepValuesState } from '~/types/claim.types';

import styles from '../ClaimReturnRegisterSecondStepContent.module.scss';

type TypeReturnOrderItems = TypeReturnRegisterSecondStepValues['returnOrderItems'];
type TypeReturnOrderItemsState = TypeReturnRegisterSecondStepValuesState['returnOrderItems'];

const getRowId = (row: TypeReturnGoodsRow) => `${row.itemName}-${row.itemId}`;

const ClaimReturnRegisterSecondStepReturnGoodsTable = ({
  data,
  dataState,
  onChange,
  onChangeState,
}: {
  data: TypeReturnOrderItems;
  dataState: TypeReturnOrderItemsState;
  onChange: (values: Partial<TypeReturnRegisterSecondStepValues>) => void;
  onChangeState: (values: Partial<TypeReturnRegisterSecondStepValuesState>) => void;
}) => {
  const selectOptions = useClaimStore((state) => state.selectOptions.returnReason);

  const latestRef = useRef({ data, dataState, onChange, onChangeState });
  useLayoutEffect(() => {
    latestRef.current = { data, dataState, onChange, onChangeState };
  });

  const handlers = useMemo<TypeReturnGoodsHandlers>(() => {
    const updateItems = (itemId: string, patch: (item: TypeReturnOrderItems[number]) => TypeReturnOrderItems[number]) =>
      latestRef.current.onChange({
        returnOrderItems: latestRef.current.data.map((item) => (item.itemId === itemId ? patch(item) : item)),
      });
    const updateStates = (
      itemId: string,
      patch: (item: TypeReturnOrderItemsState[number]) => TypeReturnOrderItemsState[number]
    ) =>
      latestRef.current.onChangeState({
        returnOrderItems: latestRef.current.dataState.map((item) => (item.itemId === itemId ? patch(item) : item)),
      });

    return {
      onToggleReturn: (itemId, checked) => {
        updateItems(itemId, (item) => ({ ...item, isReturn: checked, returnQuantity: '', returnReason: null }));
        updateStates(itemId, (item) => ({
          ...item,
          returnQuantity: DEFAULT_INPUT_STATUS,
          returnReason: DEFAULT_INPUT_STATUS,
        }));
      },
      onChangeQuantity: (itemId, value) => {
        updateItems(itemId, (item) => ({ ...item, returnQuantity: value }));
        updateStates(itemId, (item) => ({ ...item, returnQuantity: DEFAULT_INPUT_STATUS }));
      },
      onBlurQuantity: (itemId, value) => {
        const numberValue = Number(value.trim());
        const quantity = latestRef.current.data.find((item) => item.itemId === itemId)?.quantity ?? 0;

        if (numberValue === 0) {
          updateItems(itemId, (item) => ({ ...item, returnQuantity: '' }));
          return;
        }

        if (numberValue > quantity) {
          updateStates(itemId, (item) => ({ ...item, returnQuantity: { status: 'error' } }));

          dialogService.addDialog({
            children: '반품수량은 구매수량보다 같거나 적은 수량만 입력할 수 있습니다.',
            onConfirm: () => {
              updateItems(itemId, (item) => ({ ...item, returnQuantity: '' }));
              updateStates(itemId, (item) => ({ ...item, returnQuantity: DEFAULT_INPUT_STATUS }));
            },
          });
        }
      },
      onFocusQuantity: (itemId) => updateStates(itemId, (item) => ({ ...item, returnQuantity: DEFAULT_INPUT_STATUS })),
      onChangeReason: (itemId, value) => {
        updateItems(itemId, (item) => ({ ...item, returnReason: value }));
        updateStates(itemId, (item) => ({ ...item, returnReason: DEFAULT_INPUT_STATUS }));
      },
    };
  }, []);

  const { columnDefs } = useMemo(
    () => getClaimReturnRegisterReturnItemsColumnDefs({ selectOptions, handlers }),
    [selectOptions, handlers]
  );

  const rows = useMemo<TypeReturnGoodsRow[]>(
    () => data.map((item) => ({ ...item, state: dataState.find((state) => state.itemId === item.itemId) })),
    [data, dataState]
  );

  return (
    <div className={styles['table-wrapper']}>
      <Table data={rows} columns={columnDefs} getRowId={getRowId} keepScrollOnDataChange />
    </div>
  );
};

export default ClaimReturnRegisterSecondStepReturnGoodsTable;
